import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Waves, Trophy, CalendarDays, Users2, CheckCircle2, ArrowRight, Calendar, Clock } from 'lucide-react'
import axios from 'axios'
import moment from 'moment'

const Home = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [poolSettings, setPoolSettings] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch upcoming events within 2 weeks and pool settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch events
        const eventsResponse = await axios.get('/api/events')
        const now = moment()
        const twoWeeksFromNow = moment().add(2, 'weeks')

        // Filter events that are within the next 2 weeks
        const upcoming = eventsResponse.data.filter(event => {
          const eventDate = moment.utc(event.eventDate).local()
          return eventDate.isAfter(now) && eventDate.isBefore(twoWeeksFromNow)
        })

        // Sort by date (soonest first)
        upcoming.sort((a, b) => moment.utc(a.eventDate).diff(moment.utc(b.eventDate)))

        setUpcomingEvents(upcoming)

        // Fetch pool settings
        const poolResponse = await axios.get('/api/pool-settings')
        setPoolSettings(poolResponse.data)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleRsvpClick = (event) => {
    // Navigate to events page with the event ID
    window.location.href = `/events#${event._id}`
  }
  const features = [
    {
      title: 'Junior Olympic Pool',
      description: 'Experience our 6-lane, 25-meter Junior Olympic pool with separate diving well and gated wading pool for the little ones.',
      icon: <Waves className="w-8 h-8" />,
      link: '/pool'
    },
    {
      title: '4 Lighted Tennis Courts',
      description: 'Play day or night on our professional tennis courts. Home to ALTA, USTA, and T2 teams year-round.',
      icon: <Trophy className="w-8 h-8" />,
      link: '/tennis'
    },
    {
      title: '9-Acre Park Setting',
      description: 'Enjoy our beautifully maintained grounds with picnic areas, grills, playground, and ample seating throughout.',
      icon: <CalendarDays className="w-8 h-8" />,
      link: '/facility-reservation'
    },
    {
      title: 'Year-Round Community',
      description: 'Join us for social events, swim meets, tennis tournaments, and community gatherings throughout the year.',
      icon: <Users2 className="w-8 h-8" />,
      link: '/events'
    }
  ]

  const facilities = [
    'Junior Olympic 6-lane swimming pool with diving board',
    'Separate gated wading pool for toddlers',
    'Four professionally maintained lighted tennis courts',
    'Taj Mahal pavilion and standard pavilion',
    'Playground and extensive picnic areas',
    'Professional lifeguards during swim season',
    'Covered structures for private events',
    'Large grass areas and raised deck'
  ]

  return (
    <div className="min-h-screen">
      {/* Upcoming Events Banner */}
      {!loading && upcomingEvents.length > 0 && (
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-4 shadow-lg">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Calendar className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-2">Upcoming Events</h3>
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map(event => (
                      <div key={event._id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <div className="flex items-center gap-2 text-primary-100">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {moment.utc(event.eventDate).format('MMM DD, YYYY')}
                            {event.startTime && ` at ${moment(event.startTime, 'HH:mm').format('h:mm A')}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-semibold">{event.title}</span>
                          {(event.requireRsvp || event.collectPayment) && (
                            <Link
                              to="/events"
                              className="inline-flex items-center px-3 py-1 bg-white text-primary-700 rounded-full text-sm font-medium hover:bg-primary-50 transition-colors"
                            >
                              {event.collectPayment ? 'RSVP & View Pricing' : 'RSVP'}
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {upcomingEvents.length > 3 && (
                    <Link
                      to="/events"
                      className="inline-flex items-center text-primary-100 hover:text-white text-sm font-medium mt-3 transition-colors"
                    >
                      View all {upcomingEvents.length} upcoming events
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{backgroundImage: 'url(/images/LFST_10.JPG)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-secondary-800/70 to-tertiary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Welcome to Lockridge Forest Swim Tennis Club
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              A premier members-only private club serving Peachtree Corners, Dunwoody, Norcross,
              and neighboring communities since the early 1970s
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/membership" className="btn btn-primary bg-white text-primary-700 hover:bg-gray-100">
                Join Our Community
              </Link>
              <Link to="/about-us" className="btn btn-outline border-white text-white hover:bg-white hover:text-primary-700">
                About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="mb-4 text-gray-900 text-3xl md:text-4xl font-bold">Club Highlights</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover what makes Lockridge Forest the perfect destination for families
              seeking recreation, relaxation, and community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="bg-white rounded-lg shadow-md hover:shadow-xl p-6 hover:scale-105 transition-all duration-300 group"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{feature.description}</p>
                  <span className="text-primary-500 font-medium group-hover:text-primary-700 flex items-center">
                    Learn more
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities Overview */}
      <section className="py-10 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="mb-6 text-gray-900 text-3xl md:text-4xl font-bold">World-Class Facilities</h2>
              <p className="text-lg text-gray-600 mb-6">
                Nestled in a beautiful 9-acre park-like setting, our club offers an unparalleled
                recreational experience for the entire family. From sunrise swims to evening tennis
                matches, every day brings new opportunities for fun and fitness.
              </p>
              <ul className="space-y-3">
                {facilities.map((facility, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{facility}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src="/images/pool_1.jpg"
                  alt="Pool facilities"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/images/tennis_2.jpeg"
                    alt="Tennis courts"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src="/images/Taj_1.jpg"
                    alt="Taj Mahal pavilion"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-10 bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="mb-6 text-3xl md:text-4xl font-bold">Ready to Join Our Community?</h2>
            <p className="text-xl mb-8 opacity-95">
              Experience the best summer of your life at Lockridge Forest Swim Tennis Club.
              {poolSettings?.openingDate && ` Pool Opens ${poolSettings.openingDate} - don't miss out!`}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold mb-2">9</div>
                  <div className="text-sm opacity-90">Acres of Recreation</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">4</div>
                  <div className="text-sm opacity-90">Lighted Tennis Courts</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">45+</div>
                  <div className="text-sm opacity-90">Years of Community</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Link to="/membership" className="btn btn-primary bg-white text-primary-700 hover:bg-gray-100">
                View Membership Options
              </Link>
              <a href="mailto:admin@lockridgeforestswimtennis.com" className="btn btn-outline border-white text-white hover:bg-white hover:text-primary-700">
                Contact Us
              </a>
            </div>
            <div className="text-sm opacity-90">
              <p>3154 Sumac Drive, Peachtree Corners, GA 30360</p>
              <p className="mt-2">
                <a href="mailto:admin@lockridgeforestswimtennis.com" className="hover:underline text-white">
                  admin@lockridgeforestswimtennis.com
                </a>
                {' '} | {' '}
                <a href="tel:7706097428" className="hover:underline text-white">
                  (770) 609-7428 (text or leave voicemail)
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
